import { capitalizeAllIfLower, formatDate } from '@btime/helpers'
import { compose, path, prop, propOr } from 'ramda'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { Col } from '../../../../../packages/kiti/components/structure/Grid'
import { genResumeUrl } from '../../../../../utils/helpers/serviceOrder'
import { useGetServiceOrderAddress } from '../../../../../utils/hooks/serviceOrderAddress'
import { useServiceOrderPermissions } from '../../../../../utils/permissions/serviceOrders/serviceOrderPermissions'
import AssetPresenter from '../../../../../containers/assets/AssetPresenter'
import EditableServiceOrdersUser from '../../../../../containers/serviceOrders/EditableServiceOrdersUser'
import FieldValuesPresenter from '../../../../../containers/fieldValues/FieldValuesPresenter'
import FlexGrid, { FlexCol } from '../../../../../components/structure/FlexGrid'
import Gallery from '../../../../../components/presentation/gallery/Gallery'
import LabelValue from '../../../../../packages/kiti/components/presentation/LabelValue'
import LoadingWrapper from '../../../../../packages/kiti/components/helpers/LoadingWrapper'
import PlacePresenter from '../../../../../containers/places/PlacePresenter'
import ShareLink from '../../../../../components/presentation/ShareLink'
import StatusTag, { PRIORITY } from '../../../../../containers/serviceOrders/StatusTag'
import Markdown from '../../../../../components/presentation/Markdown'
import { COLORS } from '@btime/constants'
import { useSessionPermission } from '@btime/react-session'

const BasicInfo = ({ serviceOrder }) => {
  const { t } = useTranslation('serviceOrders')
  const { canSeePublicLink } = useServiceOrderPermissions(serviceOrder)
  const canSeeConfidentialFields = useSessionPermission('seeConfidentialFields')
  const [address, loading] = useGetServiceOrderAddress(serviceOrder)

  const checklistName = compose(capitalizeAllIfLower, path(['checklist', 'name']))(serviceOrder)
  const schedule = compose(formatDate('DD/MM/YYYY HH:mm'), prop('scheduling'))(serviceOrder)
  const place = propOr({}, 'place', serviceOrder)
  const asset = propOr({}, 'asset', serviceOrder)
  const serviceStatus = propOr({}, 'status', serviceOrder)
  const priority = propOr({}, 'priority', serviceOrder)

  const { description, documents } = serviceOrder
  const resumeURL = genResumeUrl(serviceOrder)

  return (
    <FlexGrid justify="flex-start" align="flex-start" paddingGutter={0.5} wrap>
      <FlexCol width="50%">
        <LabelValue label={t('show.userOrGroup')}>
          <EditableServiceOrdersUser serviceOrder={serviceOrder} />
        </LabelValue>
      </FlexCol>

      <FlexCol width="50%">
        {!!place?.id && (
          <LabelValue label={t('show.placeOrPerson')}>
            <PlacePresenter place={place} weight="500" isPublicPresenter />
          </LabelValue>
        )}
      </FlexCol>

      <FlexCol width="50%">
        <LabelValue label={t('fields.status')} children={<StatusTag item={serviceStatus} />} />
      </FlexCol>

      {!!asset?.id && (
        <FlexCol width="50%">
          <LabelValue label={t('show.asset')}>
            <AssetPresenter asset={asset} weight="500" />
          </LabelValue>
        </FlexCol>
      )}

      <FlexCol width="50%">
        <LabelValue label={t('show.checklist')} value={checklistName} />
      </FlexCol>

      <FlexCol width="50%">
        <LabelValue label={t('show.scheduling')} value={schedule || t('show.noDate')} />
      </FlexCol>

      <FlexCol width="50%">
        {priority.id && (
          <LabelValue
            label={t('show.priority')}
            value={!priority.id && t('show.noPriority')}
            children={<StatusTag item={priority} type={PRIORITY} />}
          />
        )}
      </FlexCol>

      <FlexCol width="100%">
        <FlexGrid justify="flex-start" align="flex-start" paddingGutter={0.5} wrap>
          {(address?.search || loading) && (
            <LoadingWrapper loading={loading}>
              <FlexCol width="100%">
                <LabelValue
                  label={t('show.address')}
                  value={`${address?.search}${address?.complement ? ` -  ${address?.complement}` : ''}`}
                />
              </FlexCol>
            </LoadingWrapper>
          )}

          {description && (
            <FlexCol width="100%">
              <LabelValue label={t('show.description')}>
                <Markdown value={description} color={COLORS.black} fontFamily="system-ui" />
              </LabelValue>
            </FlexCol>
          )}

          {canSeePublicLink && (
            <FlexCol width="100%">
              <LabelValue label={t('show.publicResumeLink')}>
                <ShareLink url={resumeURL} textOnly />
              </LabelValue>
            </FlexCol>
          )}

          <Col width="100%">
            <FieldValuesPresenter fieldValues={serviceOrder.fieldValues} hideConfidential={!canSeeConfidentialFields} />
          </Col>

          {!!documents && !!documents.length && (
            <FlexCol width="100%">
              <LabelValue label={t('show.documents')}>
                <Gallery items={documents} thumbHeight="7rem" />
              </LabelValue>
            </FlexCol>
          )}
        </FlexGrid>
      </FlexCol>
    </FlexGrid>
  )
}

export default BasicInfo
